import { useState, useEffect } from "react";
import React from "react";

// @mui material components
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Custom styles for the Configurator
import ConfiguratorRoot from "examples/Configurator/ConfiguratorRoot";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setOpenConfigurator,
  setTransparentSidenav,
  setWhiteSidenav,
  setFixedNavbar,
  setSidenavColor,
  setDarkMode,
  setLanguage,
  setCurrency,
} from "context";

function Configurator() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    openConfigurator,
    fixedNavbar,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    language,
    currency,
  } = controller;
  const [disabled, setDisabled] = useState(false);
  const sidenavColors = ["primary", "dark", "info", "success", "warning", "error"];

  // Função para carregar a cor salva
  const loadSidenavColor = () => {
    const savedColor = localStorage.getItem("sidenavColor");
    if (savedColor) {
      setSidenavColor(dispatch, savedColor);
    }
  };

  // Função para carregar o tipo do menu lateral
  const loadSidenavType = () => {
    const savedType = localStorage.getItem("sidenavType") || "dark"; // Define "dark" como padrão
    if (savedType === "transparent") {
      setTransparentSidenav(dispatch, true);
      setWhiteSidenav(dispatch, false);
    } else if (savedType === "white") {
      setWhiteSidenav(dispatch, true);
      setTransparentSidenav(dispatch, false);
    } else {
      setWhiteSidenav(dispatch, false);
      setTransparentSidenav(dispatch, false);
    }
  };

  // Função para carregar o modo escuro/claro
  const loadDarkMode = () => {
    const savedDarkMode = localStorage.getItem("darkMode") === "true";
    setDarkMode(dispatch, savedDarkMode);
  };

  // Função para carregar o estado do menu fixo
  const loadFixedNavbar = () => {
    const savedFixedNavbar = localStorage.getItem("fixedNavbar");
    const isFixed = savedFixedNavbar === null ? true : savedFixedNavbar === "true"; // Habilitado por padrão
    setFixedNavbar(dispatch, isFixed);
  };

  // Consolidar carregamento inicial (sidenavType, darkMode, fixedNavbar, language, currency)
  useEffect(() => {
    loadSidenavColor();
    loadSidenavType();
    loadDarkMode();
    loadFixedNavbar();

    // Carregar idioma e moeda salvos
    const savedLanguage = localStorage.getItem("language") || "pt"; // Padrão: Português
    const savedCurrency = localStorage.getItem("currency") || "BRL"; // Padrão: Real
    setLanguage(dispatch, savedLanguage);
    setCurrency(dispatch, savedCurrency);
  }, []);

  // Ajustar responsividade
  useEffect(() => {
    function handleDisabled() {
      return window.innerWidth > 1200 ? setDisabled(false) : setDisabled(true);
    }

    window.addEventListener("resize", handleDisabled);
    handleDisabled();

    return () => window.removeEventListener("resize", handleDisabled);
  }, []);

  const handleCloseConfigurator = () => setOpenConfigurator(dispatch, false);

  const handleTransparentSidenav = () => {
    setTransparentSidenav(dispatch, true);
    setWhiteSidenav(dispatch, false);
    localStorage.setItem("sidenavType", "transparent");
  };

  const handleWhiteSidenav = () => {
    setWhiteSidenav(dispatch, true);
    setTransparentSidenav(dispatch, false);
    localStorage.setItem("sidenavType", "white");
  };

  const handleDarkSidenav = () => {
    setWhiteSidenav(dispatch, false);
    setTransparentSidenav(dispatch, false);
    localStorage.setItem("sidenavType", "dark");
  };

  const handleFixedNavbar = () => {
    const newFixedNavbar = !fixedNavbar;
    setFixedNavbar(dispatch, newFixedNavbar);
    localStorage.setItem("fixedNavbar", newFixedNavbar);
  };

  const handleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(dispatch, newDarkMode);
    localStorage.setItem("darkMode", newDarkMode);
  };

  const sidenavTypeButtonsStyles = ({
    functions: { pxToRem },
    palette: { white, dark, background },
    borders: { borderWidth },
  }) => ({
    height: pxToRem(39),
    background: darkMode ? background.sidenav : white.main,
    color: darkMode ? white.main : dark.main,
    border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,
    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? background.sidenav : white.main,
      color: darkMode ? white.main : dark.main,
      border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,
    },
  });

  const sidenavTypeActiveButtonStyles = ({
    functions: { pxToRem, linearGradient },
    palette: { white, gradients, background },
  }) => ({
    height: pxToRem(39),
    background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
    color: darkMode ? background.sidenav : white.main,
    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
      color: darkMode ? background.sidenav : white.main,
    },
  });

  // Configuração de Idioma
  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    setLanguage(dispatch, newLanguage);
    localStorage.setItem("language", newLanguage);
  };

  // Configuração de Moeda
  const handleCurrencyChange = (newCurrency) => {
    setCurrency(dispatch, newCurrency);
    localStorage.setItem("currency", newCurrency);
  };

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="baseline" pt={4} pb={0.5} px={3}>
        <MDBox>
          <MDTypography variant="h5">Configurações</MDTypography>
        </MDBox>

        <Icon
          sx={({ typography: { size }, palette: { dark, white } }) => ({
            fontSize: `${size.lg} !important`,
            color: darkMode ? white.main : dark.main,
            stroke: "currentColor",
            strokeWidth: "2px",
            cursor: "pointer",
            transform: "translateY(5px)",
          })}
          onClick={handleCloseConfigurator}
        >
          close
        </Icon>
      </MDBox>

      <Divider />

      <MDBox pt={0.5} pb={3} px={3}>
        <MDBox>
          <MDTypography variant="h6">Cores</MDTypography>
          <MDBox mb={0.5}>
            {sidenavColors.map((color) => (
              <IconButton
                key={color}
                sx={({ borders: { borderWidth }, palette: { white, dark, background }, transitions }) => ({
                  width: "24px",
                  height: "24px",
                  padding: 0,
                  border: `${borderWidth[1]} solid ${darkMode ? background.sidenav : white.main}`,
                  borderColor: sidenavColor === color ? (darkMode ? white.main : dark.main) : "",
                  transition: transitions.create("border-color", {
                    easing: transitions.easing.sharp,
                    duration: transitions.duration.shorter,
                  }),
                  backgroundImage: ({ functions: { linearGradient }, palette: { gradients } }) =>
                    linearGradient(gradients[color].main, gradients[color].state),
                  "&:not(:last-child)": {
                    mr: 1,
                  },
                  "&:hover, &:focus, &:active": {
                    borderColor: darkMode ? white.main : dark.main,
                  },
                })}
                onClick={() => {
                  setSidenavColor(dispatch, color);
                  localStorage.setItem("sidenavColor", color);
                }}
              />
            ))}
          </MDBox>
        </MDBox>

        <MDBox mt={3} lineHeight={1}>
          <MDTypography variant="h6">Menu Lateral</MDTypography>
          <MDTypography variant="button" color="text"></MDTypography>
          <MDTypography variant="caption" color="text" mt={0.5} display="block">
            Apenas na versão web.
          </MDTypography>

          <MDBox sx={{ display: "flex", mt: 2, mr: 1 }}>
            <MDButton
              color="dark"
              variant="gradient"
              onClick={handleDarkSidenav}
              disabled={disabled}
              fullWidth
              sx={!transparentSidenav && !whiteSidenav ? sidenavTypeActiveButtonStyles : sidenavTypeButtonsStyles}
            >
              Escuro
            </MDButton>
            <MDBox sx={{ mx: 1, width: "8rem", minWidth: "8rem" }}>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={handleTransparentSidenav}
                disabled={disabled}
                fullWidth
                sx={transparentSidenav && !whiteSidenav ? sidenavTypeActiveButtonStyles : sidenavTypeButtonsStyles}
              >
                Transparente
              </MDButton>
            </MDBox>
            <MDButton
              color="dark"
              variant="gradient"
              onClick={handleWhiteSidenav}
              disabled={disabled}
              fullWidth
              sx={whiteSidenav && !transparentSidenav ? sidenavTypeActiveButtonStyles : sidenavTypeButtonsStyles}
            >
              Claro
            </MDButton>
          </MDBox>
        </MDBox>

        <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={3} lineHeight={1}>
          <MDTypography variant="h6">Menu Superior Fixo</MDTypography>
          <Switch checked={fixedNavbar} onChange={handleFixedNavbar} />
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
          <MDTypography variant="h6">Modo Escuro/Claro</MDTypography>
          <Switch checked={darkMode} onChange={handleDarkMode} />
        </MDBox>
        <Divider />

        <MDBox mt={3} lineHeight={1}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6">Idioma</MDTypography>
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDTypography variant="button" color={language === "pt" ? "text" : "secondary"}>
                PT
              </MDTypography>
              <Switch
                checked={language === "en"}
                onChange={(e) => setLanguage(dispatch, e.target.checked ? "en" : "pt")}
                inputProps={{ "aria-label": "Idioma" }}
              />
              <MDTypography variant="button" color={language === "en" ? "text" : "secondary"}>
                EN
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>

        <Divider sx={{ my: 3 }} />

        <MDBox mt={3} lineHeight={1}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6">Moeda</MDTypography>
            <MDBox display="flex" gap={0.5}>
              <MDButton
                color="dark"
                variant={currency === "BRL" ? "gradient" : "outlined"}
                onClick={() => handleCurrencyChange("BRL")} // Usar handleCurrencyChange
                sx={{
                  height: "28px",
                  minWidth: "40px",
                  fontSize: "0.7rem",
                  fontWeight: "400",
                  padding: "2px 8px",
                  textTransform: "uppercase",
                }}
              >
                R$
              </MDButton>
              <MDButton
                color="dark"
                variant={currency === "USD" ? "gradient" : "outlined"}
                onClick={() => handleCurrencyChange("USD")} // Usar handleCurrencyChange
                sx={{
                  height: "28px",
                  minWidth: "40px",
                  fontSize: "0.7rem",
                  fontWeight: "400",
                  padding: "2px 8px",
                  textTransform: "uppercase",
                }}
              >
                $
              </MDButton>
              <MDButton
                color="dark"
                variant={currency === "EUR" ? "gradient" : "outlined"}
                onClick={() => handleCurrencyChange("EUR")} // Usar handleCurrencyChange
                sx={{
                  height: "28px",
                  minWidth: "40px",
                  fontSize: "0.7rem",
                  fontWeight: "400",
                  padding: "2px 8px",
                  textTransform: "uppercase",
                }}
              >
                €
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>

        <MDBox mt={3} textAlign="center">
          <MDTypography
            variant="caption"
            color={darkMode ? "white" : "text"}
            sx={{ whiteSpace: "normal", wordWrap: "break-word" }}
          >
            © 2024 Finnoplan. Todos os direitos reservados. <br />
            Versão 2.0.0
          </MDTypography>
        </MDBox>
      </MDBox>
    </ConfiguratorRoot>
  );
}

export default Configurator;
